/**
 * setupLedger.ts — the ledger's one derivation, and it derives nothing the page already knows.
 *
 * THE LEDGER IS A THIRD VIEW OF `computeSetupState`, NOT A SECOND OPINION. Every number in the
 * standing module is `state.progress` or `state.areaGroups` read verbatim — the same objects the
 * manifest column draws — so the two cannot disagree about how much of this contract is done. The
 * page has been through that already: the screen this one replaced derived completion in four
 * places and the four gave different answers, which is the whole reason `setupState.ts` exists.
 * A stats column that recounted the rows for itself would be the fifth.
 *
 * WHAT IT DOES ADD is the two facts the manifest genuinely does not carry, both of them read
 * straight off rows the page already holds:
 *
 * - HOW LONG THIS HAS BEEN OPEN — `contracts.created_at`, which no row prints;
 * - WHAT IS AT STAKE — the four money lines, off the snapshot's own facts.
 *
 * IT ALSO WIDENS ITS OWN INPUTS BY EXACTLY ONE ROW SET — `agreement_tokens`, for the `expires_at`
 * that `snapshotAdapter` drops on its way into `SetupSnapshot`. The adapter carries exactly what
 * the manifest rows need, and the raw tokens are already at the mount point on `agreements.tokens`.
 *
 * PURE, REACT-FREE, AND `now` IS AN INPUT — the same three properties `setupState.ts` holds, for
 * the same reason: vitest runs node-env over `.ts` only, and a column whose day counts came off a
 * clock could not be pinned by a test at all.
 *
 * EVERY FIELD DEGRADES AND NOTHING THROWS. The page mounts this before any read has answered, and
 * a fresh DRAFT legitimately has no tokens, no deposits and no money. Absent is `null` or `[]` all
 * the way down, and the component omits what it is handed nothing for.
 */

import { differenceInCalendarDays, format, isValid, parseISO } from 'date-fns';

import { businessDay, formatCurrency } from '@/lib/format';
import type { AgreementTokenRow } from '@/lib/agreements/api';
import { instrumentFor } from '@/lib/agreements/instrument';

import { groupCounter } from '../manifest/manifestView';
import type { ManifestRowId, SetupSnapshot, SetupState } from '../setupState';
import type { SetupArea } from '../steps';

// ─── The view model ──────────────────────────────────────────────────────────

export interface LedgerAreaLine {
    area: SetupArea;
    label: string;
    /**
     * THE RECEIPT SUBSTITUTION, AND IT IS THE LEDGER'S OWN RULE RATHER THAN THE MANIFEST'S.
     *
     * The ledger substitutes the receipt for EVERY satisfied group; the manifest does so only when
     * the group is COLLAPSED (`Manifest.tsx`: `!open && group.receipt`). The ledger is a summary
     * and not a nav — it has no expanded group, nothing in it opens, and a partial `n/m` under a
     * heading that says where the contract stands is a number nobody can act on — so `satisfied`
     * alone decides it here and it never prints a partial counter.
     *
     * THE ONE VISIBLE DIVERGENCE, AND IT IS DESIGNED: on the ACTIVE area, if that area is
     * satisfied, the manifest's expanded group prints `4/4` and the ledger's line prints the dated
     * receipt. Two renderings of one fact, each right for its own column — the manifest is showing
     * you the rows you are standing in, the ledger is telling you the area is done.
     */
    receipt: string | null;
    /** `groupCounter(group)`, verbatim. Null exactly where the receipt is printed instead. */
    counter: string | null;
    /**
     * One segment per counted row, settled first — a countable track, never a percentage and
     * never an arc. Empty on a satisfied group, and empty on a group with nothing to count.
     */
    segments: boolean[];
}

export interface LedgerStanding {
    /** `state.progress`, verbatim. */
    areasComplete: number;
    areaCount: number;
    needYou: number;
    /**
     * THE WAITING LIST'S OWN LENGTH — one derivation for the figure and for the list beneath it,
     * so the module can never say `3 waiting on others` over two rows. See `waitingOf`.
     */
    waitingOnOthers: number;
    areas: LedgerAreaLine[];
}

export interface LedgerDays {
    /** Whole calendar days since `contracts.created_at`. Never negative. */
    days: number;
    /** `Opened Jul 24, 2026`. */
    openedText: string;
}

export interface LedgerStakeLine {
    key: string;
    value: string;
    /** The date or the term under the figure, where the figure has one. */
    sub: string | null;
}

export interface LedgerWait {
    rowId: ManifestRowId;
    /**
     * `meta.actor ?? row.label` — the line's subject, and NEVER INVENTED. A wait that names no
     * person is still a wait, and it returns to the list under the requirement's own words
     * (`Company countersignature`) rather than under a fabricated `Somebody`.
     */
    subject: string;
    /** The person the row names, or null where it names none — the subject is the label then. */
    actor: string | null;
    /**
     * The row's own label, as the muted qualifier UNDER the subject — which is what tells two
     * waits on one person apart. Null exactly where the label IS the subject, so the line never
     * prints the same words twice.
     */
    label: string | null;
    /** Whole days the link has been out. Null when the row does not carry one. */
    elapsedDays: number | null;
    openCount: number | null;
    /** The token minted for this wait is past `expires_at` and was never consumed. */
    linkExpired: boolean;
    /** 0…1 against the LONGEST wait on the list — a comparison, not a percentage. */
    barFraction: number | null;
}

/**
 * The whole column, in the owner's module order: standing, days, at stake, waiting on.
 *
 * THE MONEY RISES ABOVE THE FOLD (owner's ruling). At 1440 the ledger's scrollport is ~918px and
 * the first three modules fill about two thirds of it, so what an admin sees without scrolling is
 * where the contract stands, how long it has taken and what it is worth. The tail module is the one
 * that rewards a scroll — a wait list nobody reads twice — so it takes it.
 *
 * ONE ORDER SERVES BOTH HOMES. Below 1440 the same four modules render as a fixed-measure grid at
 * the foot of the canvas, where this order is the READING order (row-major: standing, days / stake,
 * waiting) — so the money is still the third thing read, and neither form has an order of its own
 * to keep in step.
 */
export interface LedgerView {
    standing: LedgerStanding | null;
    days: LedgerDays | null;
    stake: LedgerStakeLine[];
    waiting: LedgerWait[];
}

export interface LedgerInput {
    state?: SetupState | null;
    snapshot?: SetupSnapshot | null;
    /** `agreement_tokens`, raw — `expires_at`, which the adapter drops. */
    tokens?: AgreementTokenRow[] | null;
    contractCreatedAt?: string | null;
    /** The clock, as an input. Defaults to the wall clock only when nothing is handed in. */
    now?: Date | string | null;
}

// ─── Small helpers, the same shapes `setupState.ts` uses ─────────────────────

function asDate(value: string | Date | null | undefined): Date | null {
    if (!value) return null;
    const date = value instanceof Date ? value : parseISO(value);
    return isValid(date) ? date : null;
}

/** `Jul 24, 2026` — the app's one date shape. Unparseable prints nothing. */
function longDate(value: string | Date | null | undefined): string {
    const date = asDate(value);
    return date ? format(date, 'MMM d, yyyy') : '';
}

/**
 * `Jul 24` — the short form the 222px column can afford beside a figure, on the BUSINESS day.
 *
 * `businessDay`, NOT `asDate`, for the reason `setupState`'s `dateText` gives: the money dates this
 * renders are a TIMESTAMPTZ (`transactions.transaction_at`) and a DATE string
 * (`contract_instalments.starts_at`), and reading the instant in the HOST zone dated a payment a day
 * early everywhere west of Greenwich. The pattern is unchanged — `businessDay` hands back the Ghana
 * calendar day as a Date at local midnight.
 */
function shortDate(value: string | Date | null | undefined): string {
    const date = businessDay(value);
    return date ? format(date, 'MMM d') : '';
}

function money(amount: number | null | undefined): string | null {
    return amount === null || amount === undefined ? null : formatCurrency(amount);
}

// ─── 1 · WHERE THIS CONTRACT STANDS ──────────────────────────────────────────

function standingOf(
    state: SetupState | null | undefined,
    waitingCount: number,
): LedgerStanding | null {
    if (!state) return null;
    const progress = state.progress;
    const groups = Array.isArray(state.areaGroups) ? state.areaGroups : [];

    return {
        // VERBATIM, ALL THREE.
        areasComplete: progress?.areasComplete ?? 0,
        areaCount: progress?.areaCount ?? groups.length,
        needYou: progress?.needYou ?? 0,
        // AND THE FOURTH IS THE LIST'S OWN LENGTH, which is the one thing it may not take
        // verbatim: `progress.waitingOnOthers` counts every waiting row including the transient
        // seal, and the list below deliberately does not draw one. Reading the figure off the
        // list is what stops this module from printing a number the rows under it contradict.
        waitingOnOthers: waitingCount,
        areas: groups.map((group) => {
            if (group.satisfied) {
                return {
                    area: group.area,
                    label: group.label,
                    receipt: group.receipt,
                    counter: null,
                    segments: [],
                };
            }
            // A GROUP WITH NOTHING TO COUNT DRAWS NO TRACK. `requiredCount` is 0 only where every
            // row in the group is dormant, which nothing declares today — but a zero here would
            // otherwise ask for a track of zero segments and a `0/0` beside it, and one of those
            // two renders as a 222px blank line.
            const total = Math.max(0, group.requiredCount ?? 0);
            const done = Math.min(Math.max(0, group.doneCount ?? 0), total);
            return {
                area: group.area,
                label: group.label,
                receipt: null,
                // THE MIRROR TEST'S SUBJECT: the string the manifest prints, from the manifest's
                // own function. Recomposing `${done}/${total}` here would be a second spelling of
                // one number, and the two would diverge the first time the counter's rule moved.
                counter: groupCounter(group),
                segments: Array.from({ length: total }, (_unused, index) => index < done),
            };
        }),
    };
}

// ─── 2 · DAYS IN SETUP ───────────────────────────────────────────────────────

function daysOf(contractCreatedAt: string | null | undefined, now: Date): LedgerDays | null {
    // `businessDay`, NOT `asDate`, FOR BOTH ENDS — the count and the date it is printed beside.
    // `17 days · Opened Jul 17, 2026` is one claim in two spellings, and off host-zone Dates the
    // two were free to disagree: west of Greenwich the rendered day slipped back to the 16th, east
    // of it the count gained a day. Both go through the Ghana calendar now.
    const opened = businessDay(contractCreatedAt);
    const today = businessDay(now);
    if (!opened || !today) return null;
    return {
        // CALENDAR DAYS, THE SAME MEASURE EVERY WAIT ON THIS PAGE IS COUNTED IN
        // (`agreementCeremony`'s `elapsedDays`). Clamped at zero: a contract created a few seconds
        // into the future by a clock skew is zero days old, never minus one.
        days: Math.max(0, differenceInCalendarDays(today, opened)),
        openedText: `Opened ${longDate(opened)}`,
    };
}

// ─── 3 · AT STAKE ────────────────────────────────────────────────────────────

/**
 * The four money lines, off the snapshot's own facts.
 *
 * `overdueRate` IS DELIBERATELY ABSENT. It is NOT NULL DEFAULT 365 in SQL, so it can never be
 * missing — it is a check, never a fill, and the financials canvas is where a check belongs. A
 * ledger line for a number that is the same on every contract is a line nobody reads twice.
 *
 * THE LOAN IS THE FIRST-JUNCTION LOAN BY CONSTRUCTION [D-B17]: `snapshotAdapter` takes
 * `firstOf(insurance.loans)` off the junction `useContractInsurance` resolved, so this reads the
 * one loan the rest of the page is already talking about rather than picking one of its own.
 */
function stakeOf(snapshot: SetupSnapshot | null | undefined): LedgerStakeLine[] {
    if (!snapshot) return [];
    const lines: LedgerStakeLine[] = [];

    const price = money(snapshot.financials?.sellingPrice);
    if (price) lines.push({ key: 'Selling price', value: price, sub: null });

    const deposits = Array.isArray(snapshot.deposits) ? snapshot.deposits : [];
    if (deposits.length > 0) {
        const total = deposits.reduce((sum, deposit) => sum + (deposit.amount ?? 0), 0);
        lines.push({
            key: 'Deposit received',
            value: formatCurrency(total),
            // ONE DEPOSIT CARRIES ITS DATE, SEVERAL CARRY THEIR COUNT — the same split the
            // `dep-payment` row makes, because a single date under a total of two payments would
            // be attributing the whole sum to the day the first one landed.
            sub: deposits.length === 1 ? shortDate(deposits[0].paidOn) || null : `${deposits.length} payments`,
        });
    }

    const instalment = snapshot.financials?.instalment ?? null;
    const instalmentAmount = money(instalment?.amount);
    if (instalmentAmount) {
        const from = shortDate(instalment?.startsAt);
        lines.push({
            key: 'Instalment',
            value: instalmentAmount,
            sub: from ? `weekly · from ${from}` : 'weekly',
        });
    }

    /**
     * THE PREMIUM LINE IS NAMED FOR WHAT HAPPENED TO IT [D-B45].
     *
     * The figure is the same either way — `insurance_loans.amount` enters `total_value` gross
     * whether Primelane advanced it or the driver paid it — so what changes is only the key. A
     * stake line headed "Premium advance" over a year the driver settled in full would state a debt
     * on the one surface an admin reads for what this contract is worth.
     *
     * THE INSTRUMENT COMES OFF THE LOAN'S OWN TWO LIMBS, the same pair the board and the server
     * read: deposits covering the amount within half a pesewa, and no instalment row.
     *
     * AND THE FIGURE STAYS THE PREMIUM UNDER [D-B46], WHICH MOVED THE ADVANCE AND NOT THIS LINE.
     * The Schedule now states the amount advanced net of what the driver paid; this column states
     * what the contract is WORTH, and `total_value` takes the premium in gross either way — which
     * is the invariant three paragraphs up. So the deposit goes in the `sub`, where it says which
     * part of the figure above it the driver has already found, rather than into the figure, where
     * it would put this column a deposit below the number every other surface reports.
     */
    const insuranceLoan = snapshot.insurance?.loan ?? null;
    const loan = money(insuranceLoan?.amount);
    if (loan) {
        const instrument = instrumentFor({
            amount: insuranceLoan?.amount ?? null,
            depositTotal: insuranceLoan?.depositTotal ?? null,
            instalmentCount: insuranceLoan ? insuranceLoan.instalments.length : null,
        });
        const deposit = insuranceLoan?.depositTotal ?? 0;
        lines.push({
            key: instrument === 'PAYMENT_RECORD' ? 'Premium paid in full' : 'Premium advance',
            value: loan,
            // NOTHING ON A PREMIUM PAID IN FULL: the key already says the driver paid all of it,
            // and `of which … paid by the driver` under it would be the same sentence twice.
            sub:
                instrument === 'PAYMENT_RECORD' || deposit <= 0
                    ? null
                    : `of which ${formatCurrency(deposit)} paid by the driver`,
        });
    }

    return lines;
}

// ─── 4 · WAITING ON ──────────────────────────────────────────────────────────

/**
 * The token minted for one wait, by the exact string the row's own `sentAt` came from.
 *
 * `snapshotAdapter` sets `TokenFact.sentAt = token.created_at` and nothing else touches it, so
 * matching on that value is an identity rather than a heuristic — and it is the only join
 * available: a `ManifestRow` carries no document id, deliberately, because the row's subject is
 * the requirement rather than the paper.
 */
function tokenForWait(
    tokens: AgreementTokenRow[] | null | undefined,
    sentAt: string | undefined,
): AgreementTokenRow | null {
    if (!sentAt || !Array.isArray(tokens)) return null;
    return tokens.find((token) => !token.revoked_at && token.created_at === sentAt) ?? null;
}

/**
 * THE LIST, AND THE STANDING MODULE'S `waiting on others` FIGURE — one derivation, not two.
 *
 * IT EXCLUDES EXACTLY ONE THING: the transient seal. `sealElapsedSeconds` is the marker of the one
 * waiting shape that finishes on its own — the activation panel already counts it in seconds and
 * the NARRATOR SPEAKS IT ("sealing…"), so a column that also named Primelane for twelve seconds
 * would be sending an admin to chase a background job that will be over before they arrive. That
 * is the whole gap between this length and `state.progress.waitingOnOthers`, which counts it.
 *
 * EVERYTHING ELSE RETURNS, INCLUDING THE ACTOR-LESS WAITS. Two waiting shapes carry no actor on
 * purpose — a countersignature blocked by another instrument [D-B18, D-B27], and a document with no
 * pending seat at all — and dropping them made the module quietly under-report the very thing it
 * is for. They are listed under the requirement's own label; nobody is invented for them.
 */
function waitingOf(
    state: SetupState | null | undefined,
    tokens: AgreementTokenRow[] | null | undefined,
    now: Date,
): LedgerWait[] {
    const rows = state?.rows ?? [];

    const waits = rows
        .filter((row) => row.state === 'waiting' && row.meta.sealElapsedSeconds === undefined)
        .map((row) => ({
            rowId: row.id,
            subject: row.meta.actor ?? row.label,
            actor: row.meta.actor ?? null,
            // The qualifier is dropped where it would repeat the subject verbatim.
            label: row.meta.actor ? row.label : null,
            elapsedDays: row.meta.elapsedDays ?? null,
            openCount: row.meta.openCount ?? null,
            token: tokenForWait(tokens, row.meta.sentAt),
        }));

    // THE BAR IS A COMPARISON AGAINST THE LONGEST WAIT, and the day count is printed beside it —
    // so the bar carries the RATIO and the text carries the fact, and neither is a percentage of
    // anything. A row with no elapsed figure gets no bar rather than a zero-width one, which
    // would read as "waited no time at all".
    const longest = waits.reduce(
        (max, wait) => (wait.elapsedDays != null && wait.elapsedDays > max ? wait.elapsedDays : max),
        0,
    );

    return waits.map((wait) => {
        const expiresAt = asDate(wait.token?.expires_at);
        return {
            rowId: wait.rowId,
            subject: wait.subject,
            actor: wait.actor,
            label: wait.label,
            elapsedDays: wait.elapsedDays,
            openCount: wait.openCount,
            // A CONSUMED LINK IS NOT AN EXPIRED ONE. `agreement_token_consume` stamps
            // `consumed_at`, and a token that has done its job has nothing left to expire.
            linkExpired: !!expiresAt && !wait.token?.consumed_at && expiresAt.getTime() < now.getTime(),
            barFraction:
                wait.elapsedDays == null || longest <= 0
                    ? null
                    : Math.min(1, Math.max(0, wait.elapsedDays / longest)),
        };
    });
}

// ─── The whole column ────────────────────────────────────────────────────────

export function setupLedger({
    state,
    snapshot,
    tokens,
    contractCreatedAt,
    now,
}: LedgerInput = {}): LedgerView {
    /**
     * THE PAGE'S CLOCK, NOT A SECOND ONE. `useContractSetupState` puts the instant it built the
     * snapshot on `snapshot.now`, and every elapsed figure on the manifest is measured from it —
     * so the ledger reading the same value is what stops "6d" in the column and "6 days ago" in
     * the narrator from being two different sixes across a midnight.
     */
    const clock = asDate(now) ?? asDate(snapshot?.now) ?? new Date();

    // THE LIST IS DERIVED FIRST because the standing module's fourth number IS its length.
    const waiting = waitingOf(state, tokens, clock);

    return {
        standing: standingOf(state, waiting.length),
        days: daysOf(contractCreatedAt, clock),
        stake: stakeOf(snapshot),
        waiting,
    };
}
